import { useState, useRef, useEffect } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useCases, useDeleteCase } from "../../../hooks/useWorkshopQueries";
import {
  Badge,
  SLABadge,
  PriorityBadge,
  PageHeader,
  Modal,
  Spinner,
  EmptyState,
  ErrorState,
  STAGE_LABELS,
  STATUS_LABELS,
  stageBadge,
  statusBadge,
  formatDate,
  timeAgo,
  sourceMeta,
  SOURCE_LABELS,
} from "../../../Component/Workshop/UI/index";
import { FilterIcon, TrashIcon } from "../../../Component/Workshop/UI/icons";

const STAGE_TABS = [
  { key: "ALL", label: "All" },
  { key: "ACTIVE", label: "Active Cases" },
  { key: "CASE_CREATED", label: "Case Created" },
  { key: "SURVEY_PROCESS", label: "Survey" },
  { key: "WORKSHOP_REPAIR", label: "Workshop" },
  { key: "DELIVERY_PAYMENT", label: "Delivery" },
  { key: "CLAIM_SETTLEMENT", label: "Settlement" },
  { key: "SLA_BREACHED", label: "SLA Breached" },
  { key: "CLOSED", label: "Closed" },
];

// Case Type options for the filter dropdown
const CASE_TYPE_LABELS = {
  Accidental: "Accidental",
  Repair: "Repair",
};

const SERVICES_FILTER_LABELS = {
  none: "🏭 Workshop only",
  crane: "🏗️ Crane only",
  recovery: "🚛 Recovery only",
  legal: "⚖️ Legal only",
  multiple: "🧩 Multiple services (2+)",
};

const SERVICE_FLAGS = [
  { key: "crane", icon: "🏗️", label: "Crane" },
  { key: "recovery", icon: "🚛", label: "Recovery" },
  { key: "legal", icon: "⚖️", label: "Legal" },
];

// Repair sub-type options — only shown once "Repair" is selected as the case type
const REPAIR_TYPE_LABELS = {
  Maintenance: "Maintenance",
  Breakdown: "Breakdown",
};

// Shared style for every dropdown in the filter bar so they size consistently
const SELECT_STYLE = { width: "auto", minWidth: 150, maxWidth: 220 };

// Icons for the services a case has (table column).
// Cases with no extra service are workshop-only, so they get a workshop icon instead of a blank dash.
function ServicesCell({ c }) {
  const active = SERVICE_FLAGS.filter(
    (s) =>
      ({ crane: c.hasCrane, recovery: c.hasRecovery, legal: c.hasLegal })[s.key],
  );
  if (!active.length)
    return (
      <span title="Workshop only" style={{ fontSize: 16 }}>
        🏭
      </span>
    );
  return (
    <span style={{ display: "inline-flex", gap: 6 }}>
      {active.map((s) => (
        <span key={s.key} title={s.label} style={{ fontSize: 16 }}>
          {s.icon}
        </span>
      ))}
    </span>
  );
}

// Small inline badge for Case Type shown in the table
function CaseTypeCell({ caseType, repairType }) {
  if (!caseType) return "—";
  const isRepair = caseType === "Repair";
  const icon = isRepair ? "🔧" : "💥";
  const label = isRepair && repairType ? `Repair · ${repairType}` : caseType;
  const variant = isRepair ? "orange" : "red";
  return (
    <Badge variant={variant}>
      {icon} {label}
    </Badge>
  );
}

export default function CasesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const globalSearch = searchParams.get("q") || "";
  const [activeTab, setActiveTab] = useState(location.state?.listTab ?? "ALL");
  const [search, setSearch] = useState(globalSearch);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [stageFilter, setStageFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [servicesFilter, setServicesFilter] = useState("");
  const [servicesWith, setServicesWith] = useState([]); // chips, only for "multiple"
  const [caseTypeFilter, setCaseTypeFilter] = useState("");
  const [repairTypeFilter, setRepairTypeFilter] = useState("");
  const [isTyping, setIsTyping] = useState(false);

  const [monthFilter, setMonthFilter] = useState(
    searchParams.get("month") || "",
  );
  const [filterOpen, setFilterOpen] = useState(false);
  const filterRef = useRef(null);
  const [casePendingDeletion, setCasePendingDeletion] = useState(null);
  const deleteCase = useDeleteCase();
  const department = (localStorage.getItem("department") || "")
    .trim()
    .toLowerCase();
  const canDeleteCases = ["admin", "management"].includes(department);

  const hasActiveDropdownFilters =
    !!statusFilter ||
    !!sourceFilter ||
    !!caseTypeFilter ||
    !!repairTypeFilter ||
    !!servicesFilter;

  const clearDropdownFilters = () => {
    setStatusFilter("");
    setSourceFilter("");
    setCaseTypeFilter("");
    setRepairTypeFilter("");
    setServicesFilter("");
    setServicesWith([]);
  };

  // ── Real query ──────────────────────────────────────────────────────────────
  const filters = {};
  if (activeTab === "SLA_BREACHED") filters.sla = "BREACHED";
  else if (activeTab === "ACTIVE") filters.caseState = "ACTIVE";
  else if (activeTab === "CLOSED") filters.caseState = "CLOSED";
  else if (activeTab !== "ALL") filters.stage = activeTab;

  if (debouncedSearch) filters.search = debouncedSearch;
  if (stageFilter) filters.stage = stageFilter;
  if (statusFilter) filters.status = statusFilter;
  if (sourceFilter) filters.source = sourceFilter;
  if (servicesFilter) filters.services = servicesFilter;
  if (servicesFilter === "multiple" && servicesWith.length)
    filters.servicesWith = servicesWith.join(",");
  if (caseTypeFilter) filters.caseType = caseTypeFilter;
  if (caseTypeFilter === "Repair" && repairTypeFilter)
    filters.repairType = repairTypeFilter;
  if (monthFilter) filters.month = monthFilter;

  const {
    data,
    isLoading,
    isError,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useCases(filters);
  const cases = data?.pages.flatMap((p) => p.cases) || [];
  const totalCount = data?.pages?.[0]?.total ?? 0;

  const highlightedCase = location.state?.highlightedCase;
  const highlightRowRef = useRef(null);

  const [activeHighlight, setActiveHighlight] = useState(highlightedCase);

  // ── Infinite scroll sentinel ──────────────────────────────────────────────
  const sentinelRef = useRef(null);

  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, cases.length]);

  useEffect(() => {
    setIsTyping(true);
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setIsTyping(false);
    }, 600);

    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (globalSearch !== search) setSearch(globalSearch);
  }, [globalSearch]);

  useEffect(() => {
    if (!highlightedCase) return;
    const t = setTimeout(() => setActiveHighlight(null), 3000);
    return () => clearTimeout(t);
  }, [highlightedCase]);

  useEffect(() => {
    if (highlightRowRef.current) {
      highlightRowRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [cases]);

  // Keep paging until the case we're returning to is loaded
  useEffect(() => {
    if (!highlightedCase) return;
    const alreadyLoaded = cases.some(
      (c) => c.accidentCaseNo === highlightedCase,
    );
    if (!alreadyLoaded && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [highlightedCase, cases, hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    if (!filterOpen) return;
    const handler = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target))
        setFilterOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [filterOpen]);

  return (
    <div
      className="wd-animate"
      style={{ display: "flex", flexDirection: "column", gap: 16 }}
    >
      {/* ── Header: title + total + month filter only ───────────────────── */}
      <PageHeader
        title="Cases"
        subtitle={
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <span style={{ whiteSpace: "nowrap" }}>
              Manage and track all accident cases
            </span>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                padding: "2px 10px",
                borderRadius: 999,
                background: "rgba(23,111,232,0.16)",
                color: "var(--wd-blue)",
                fontWeight: 700,
                fontSize: 17,
                border: "1px solid rgba(23,111,232,0.35)",
                letterSpacing: 0.2,
                whiteSpace: "nowrap",
              }}
            >
              {isLoading ? "…" : `${totalCount} Total`}
            </span>
          </span>
        }
        actions={
          <div style={{ position: "relative" }} ref={filterRef}>
            <button
              className={`wd-btn ${filterOpen || monthFilter ? "wd-btn-primary" : "wd-btn-ghost"}`}
              onClick={() => setFilterOpen((p) => !p)}
            >
              <FilterIcon /> Filters {monthFilter && "·1"}
            </button>

            {filterOpen && (
              <div
                style={{
                  position: "absolute",
                  right: 0,
                  top: "calc(100% + 8px)",
                  zIndex: 50,
                  background: "var(--wd-card)",
                  border: "1px solid var(--wd-border)",
                  borderRadius: 10,
                  padding: 16,
                  minWidth: 220,
                  boxShadow: "0 8px 24px rgba(0,0,0,.12)",
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "var(--wd-t3)",
                    marginBottom: 8,
                  }}
                >
                  FILTER BY MONTH
                </div>
                <select
                  className="wd-input wd-select"
                  value={monthFilter}
                  onChange={(e) => {
                    const val = e.target.value;
                    setMonthFilter(val);
                    setSearchParams(
                      (prev) => {
                        const next = new URLSearchParams(prev);
                        if (val) next.set("month", val);
                        else next.delete("month");
                        return next;
                      },
                      { replace: true },
                    );
                  }}
                  style={{ width: "100%" }}
                >
                  <option value="">All Months</option>
                  {Array.from({ length: 12 }, (_, i) => {
                    const d = new Date(
                      new Date().getFullYear(),
                      new Date().getMonth() - i,
                      1,
                    );
                    const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
                    return (
                      <option key={val} value={val}>
                        {d.toLocaleString("default", {
                          month: "long",
                          year: "numeric",
                        })}
                      </option>
                    );
                  })}
                </select>
                {monthFilter && (
                  <button
                    className="wd-btn wd-btn-ghost"
                    style={{ marginTop: 10, width: "100%", fontSize: 12 }}
                    onClick={() => {
                      setMonthFilter("");
                      setFilterOpen(false);
                      setSearchParams(
                        (prev) => {
                          const next = new URLSearchParams(prev);
                          next.delete("month");
                          return next;
                        },
                        { replace: true },
                      );
                    }}
                  >
                    Clear
                  </button>
                )}
              </div>
            )}
          </div>
        }
      />

      {/* ── Stage tabs ──────────────────────────────────────────────────── */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {STAGE_TABS.map((t) => (
          <button
            key={t.key}
            className={`wd-filter-tab ${activeTab === t.key ? "active" : ""}`}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Filter toolbar: dropdowns only (search is handled by the global top bar via ?q=) ── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 10,
        }}
      >
        <select
          className="wd-input wd-select"
          style={SELECT_STYLE}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All Status</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>

        <select
          className="wd-input wd-select"
          style={SELECT_STYLE}
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value)}
        >
          <option value="">All Sources</option>
          {Object.entries(SOURCE_LABELS).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>

        <select
          className="wd-input wd-select"
          style={SELECT_STYLE}
          value={caseTypeFilter}
          onChange={(e) => {
            const val = e.target.value;
            setCaseTypeFilter(val);
            if (val !== "Repair") setRepairTypeFilter("");
          }}
        >
          <option value="">All Case Types</option>
          {Object.entries(CASE_TYPE_LABELS).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>

        {caseTypeFilter === "Repair" && (
          <select
            className="wd-input wd-select"
            style={SELECT_STYLE}
            value={repairTypeFilter}
            onChange={(e) => setRepairTypeFilter(e.target.value)}
          >
            <option value="">All Repair Types</option>
            {Object.entries(REPAIR_TYPE_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        )}

        <select
          className="wd-input wd-select"
          style={SELECT_STYLE}
          value={servicesFilter}
          onChange={(e) => {
            setServicesFilter(e.target.value);
            if (e.target.value !== "multiple") setServicesWith([]);
          }}
        >
          <option value="">All Services</option>
          {Object.entries(SERVICES_FILTER_LABELS).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>

        {hasActiveDropdownFilters && (
          <button
            type="button"
            className="wd-btn wd-btn-ghost wd-btn-sm"
            onClick={clearDropdownFilters}
          >
            Clear filters
          </button>
        )}
      </div>

      {/* "Must include" chips — own row so they never squeeze the toolbar */}
      {servicesFilter === "multiple" && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 8,
            marginTop: -6,
          }}
        >
          <span style={{ fontSize: 12, color: "var(--wd-t3)" }}>
            Must include:
          </span>
          {SERVICE_FLAGS.map((s) => {
            const on = servicesWith.includes(s.key);
            return (
              <button
                key={s.key}
                type="button"
                className={`wd-btn wd-btn-sm ${on ? "wd-btn-primary" : "wd-btn-ghost"}`}
                onClick={() =>
                  setServicesWith((p) =>
                    on ? p.filter((k) => k !== s.key) : [...p, s.key],
                  )
                }
              >
                {s.icon} {s.label}
              </button>
            );
          })}
        </div>
      )}

      {/* ── Table ───────────────────────────────────────────────────────── */}
      <div className="wd-card">
        {isLoading ? (
          <Spinner />
        ) : isError ? (
          <ErrorState onRetry={() => refetch()} />
        ) : cases.length === 0 ? (
          <EmptyState message="No cases found" />
        ) : (
          <div className="wd-table-wrap">
            <table className="wd-table">
              <thead>
                <tr>
                  <th style={{ width: 48, textAlign: "center" }}>S.NO</th>
                  <th>CASE</th>
                  <th>CUSTOMER</th>
                  <th>VEHICLE</th>
                  <th>TYPE</th>
                  <th>SERVICES</th>
                  <th>PRIORITY</th>
                  <th>STAGE</th>
                  <th>STATUS</th>
                  <th>SOURCE</th>
                  <th>WORKSHOP</th>
                  <th>UPDATED</th>
                  <th>SLA</th>
                  {canDeleteCases && <th>ACTIONS</th>}
                </tr>
              </thead>
              <tbody>
                {cases.map((c, idx) => (
                  <tr
                    key={c.id}
                    ref={
                      c.accidentCaseNo === highlightedCase
                        ? highlightRowRef
                        : null
                    }
                    onClick={() => {
                      const selection = window.getSelection();
                      if (selection && selection.toString().length > 0) return;
                      navigate(`/admin/cases/${c.accidentCaseNo}`, {
                        state: { listTab: activeTab },
                      });
                    }}
                    style={{
                      transition: "background .6s, outline .6s",
                      background:
                        c.accidentCaseNo === activeHighlight
                          ? "rgba(59,130,246,.13)"
                          : undefined,
                      outline:
                        c.accidentCaseNo === activeHighlight
                          ? "1px solid var(--wd-blue)"
                          : undefined,
                    }}
                  >
                    <td
                      style={{
                        textAlign: "center",
                        color: "var(--wd-t3)",
                        fontSize: 12,
                        fontFamily: "var(--wd-mono)",
                      }}
                    >
                      {idx + 1}
                    </td>
                    <td>
                      <div
                        style={{
                          fontFamily: "var(--wd-mono)",
                          fontWeight: 600,
                          color: "var(--wd-blue)",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {c.accidentCaseNo}
                      </div>
                      <div
                        style={{
                          fontSize: 11,
                          color: "var(--wd-t3)",
                          marginTop: 2,
                        }}
                      >
                        {formatDate(c.accidentDate)}
                      </div>
                    </td>
                    <td>
                      <div style={{ fontWeight: 500 }}>
                        {c.customerName || (
                          <span
                            style={{
                              color: "var(--wd-t3)",
                              fontStyle: "italic",
                            }}
                          >
                            —
                          </span>
                        )}
                      </div>
                      <div
                        style={{
                          fontSize: 11,
                          color: "var(--wd-t3)",
                          fontFamily: "var(--wd-mono)",
                        }}
                      >
                        {c.customerPhone}
                      </div>
                    </td>
                    <td>
                      <div
                        style={{
                          fontFamily: "var(--wd-mono)",
                          fontSize: 12,
                          fontWeight: 500,
                        }}
                      >
                        {c.vehicleNo}
                      </div>
                      <div style={{ fontSize: 11, color: "var(--wd-t3)" }}>
                        {c.vehicleMake || "-"}
                      </div>
                    </td>
                    <td>
                      <CaseTypeCell
                        caseType={c.caseType}
                        repairType={c.repairType}
                      />
                    </td>
                    <td>
                      <ServicesCell c={c} />
                    </td>
                    <td>
                      <PriorityBadge priority={c.priority} />
                    </td>
                    <td>
                      <Badge variant={stageBadge(c.stage)}>
                        {STAGE_LABELS[c.stage] || c.stage}
                      </Badge>
                    </td>
                    <td>
                      <Badge variant={statusBadge(c.currentStatus)}>
                        {STATUS_LABELS[c.currentStatus] || c.currentStatus}
                      </Badge>
                    </td>
                    <td>
                      {c.source ? (
                        <Badge variant="gray">
                          {sourceMeta(c.source).icon}{" "}
                          {sourceMeta(c.source).label}
                        </Badge>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td style={{ color: "var(--wd-t2)", fontSize: 13 }}>
                      {c.workshopName || "—"}
                    </td>
                    <td style={{ color: "var(--wd-t3)", fontSize: 12 }}>
                      {timeAgo(c.updatedAt)}
                    </td>
                    <td>
                      <SLABadge status={c.slaStatus} />
                    </td>
                    {canDeleteCases && (
                      <td>
                        <button
                          className="wd-btn wd-btn-danger wd-btn-sm"
                          disabled={deleteCase.isPending}
                          onClick={(e) => {
                            e.stopPropagation();
                            setCasePendingDeletion(c);
                          }}
                          title="Delete case"
                          aria-label={`Delete case ${c.accidentCaseNo}`}
                        >
                          <TrashIcon />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            {/* Infinite scroll sentinel */}
            <div ref={sentinelRef} style={{ height: 1 }} />
            {isFetchingNextPage && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  padding: 16,
                }}
              >
                <Spinner />
              </div>
            )}
            {!hasNextPage && cases.length > 0 && (
              <div
                style={{
                  textAlign: "center",
                  padding: 12,
                  fontSize: 12,
                  color: "var(--wd-t3)",
                }}
              >
                All {totalCount} cases loaded
              </div>
            )}
          </div>
        )}
      </div>

      <Modal
        open={!!casePendingDeletion}
        onClose={() => setCasePendingDeletion(null)}
        title="Delete case"
        footer={
          <>
            <button
              className="wd-btn wd-btn-ghost"
              onClick={() => setCasePendingDeletion(null)}
            >
              Cancel
            </button>
            <button
              className="wd-btn wd-btn-danger"
              disabled={deleteCase.isPending}
              onClick={() => {
                deleteCase.mutate(casePendingDeletion.accidentCaseNo, {
                  onSettled: () => setCasePendingDeletion(null),
                });
              }}
            >
              {deleteCase.isPending ? "Deleting..." : "Delete case"}
            </button>
          </>
        }
      >
        <p style={{ margin: 0, color: "var(--wd-t2)", lineHeight: 1.6 }}>
          Delete case <strong>{casePendingDeletion?.accidentCaseNo}</strong>?
          This permanently removes the case and its related records.
        </p>
      </Modal>
    </div>
  );
}